// Column filters and sorting for list screens (docs/design/column-filters.md).
// The same shape goes from the URL to the grid header, to the list RPC
// (p_filters / p_sort, compiled by app.list_sql) and to agents as a tool
// argument. Every list declares its columns once (ListColumnSpec); anything
// a URL or a caller sends that the spec does not allow is dropped here and
// refused again by the database.

export const columnTypes = ["text", "number", "date", "enum", "bool"] as const;
export type ColumnType = (typeof columnTypes)[number];

export const filterOps = {
  text: ["contains", "starts", "eq", "empty", "notempty"],
  number: ["eq", "ge", "le", "between", "empty", "notempty"],
  date: ["eq", "ge", "le", "between", "empty", "notempty"],
  enum: ["in"],
  bool: ["is"],
} as const satisfies Record<ColumnType, readonly string[]>;
export type FilterOp = (typeof filterOps)[ColumnType][number];

export interface ListColumnSpec {
  /** Column key, the same as the RPC spec's key. */
  key: string;
  type: ColumnType;
  /** Can be sorted on the server. */
  sortable?: boolean;
  /** Can be filtered (default true). */
  filterable?: boolean;
  /** Allowed values of an enum column (checklist); unknown values are dropped. */
  values?: readonly string[];
}

export type FilterValue = string | boolean | string[];

export interface ListFilter {
  /** Column key. */
  c: string;
  o: FilterOp;
  /** Text / number / date: string; between: [from, to]; enum: values; bool: true/false; empty ops: none. */
  v?: FilterValue;
}

export interface ListSort {
  col: string;
  dir: "asc" | "desc";
}

export interface ListQuery {
  filters: ListFilter[];
  sort: ListSort | null;
}

export const MAX_FILTERS = 20;
const NUMBER = /^-?\d{1,18}(\.\d{1,10})?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function validDate(s: string): boolean {
  if (!DATE.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/** Numbers as typed in a Polish UI ("1 234,5") → "1234.5". */
export function normalizeNumber(s: string): string {
  return s.trim().replace(/\s/g, "").replace(",", ".");
}

/** A filter the spec allows, with its value normalized; null when it is not valid. */
export function validFilter(columns: readonly ListColumnSpec[], f: ListFilter): ListFilter | null {
  const col = columns.find((c) => c.key === f.c);
  if (!col || col.filterable === false) return null;
  const ops = filterOps[col.type] as readonly string[];
  if (!ops.includes(f.o)) return null;
  if (f.o === "empty" || f.o === "notempty") return { c: f.c, o: f.o };
  switch (col.type) {
    case "text": {
      const v = typeof f.v === "string" ? f.v.trim() : "";
      return v && v.length <= 200 ? { c: f.c, o: f.o, v } : null;
    }
    case "number":
    case "date": {
      const ok = (s: unknown) => typeof s === "string" && (col.type === "number" ? NUMBER.test(s) : validDate(s));
      const norm = (s: unknown) => (typeof s === "string" && col.type === "number" ? normalizeNumber(s) : s);
      if (f.o === "between") {
        if (!Array.isArray(f.v) || f.v.length !== 2) return null;
        const [a, b] = [norm(f.v[0]), norm(f.v[1])];
        return ok(a) && ok(b) ? { c: f.c, o: f.o, v: [a as string, b as string] } : null;
      }
      const v = norm(f.v);
      return ok(v) ? { c: f.c, o: f.o, v: v as string } : null;
    }
    case "enum": {
      if (!Array.isArray(f.v)) return null;
      const vs = [...new Set(f.v.filter((x) => typeof x === "string" && x.length > 0 && x.length <= 64))].filter(
        (x) => !col.values || col.values.includes(x),
      );
      return vs.length > 0 && vs.length <= 100 ? { c: f.c, o: f.o, v: vs } : null;
    }
    case "bool":
      return typeof f.v === "boolean" ? { c: f.c, o: f.o, v: f.v } : null;
  }
}

/** Keeps the valid filters, one per column (the last one wins), at most MAX_FILTERS. */
export function cleanFilters(columns: readonly ListColumnSpec[], filters: readonly ListFilter[]): ListFilter[] {
  const byCol = new Map<string, ListFilter>();
  for (const f of filters) {
    const ok = validFilter(columns, f);
    if (ok) {
      byCol.delete(ok.c);
      byCol.set(ok.c, ok);
    }
  }
  return [...byCol.values()].slice(0, MAX_FILTERS);
}

export function validSort(columns: readonly ListColumnSpec[], sort: ListSort | null): ListSort | null {
  if (!sort) return null;
  const col = columns.find((c) => c.key === sort.col);
  return col?.sortable ? sort : null;
}

// URL form ---------------------------------------------------------------------
//   f = "status:in:draft,released;amount:ge:1000;note:empty"
//   s = "-date"  (descending) or "code"
// Separators inside values are percent-escaped (% : ; ,).

const esc = (s: string) => s.replace(/[%:;,]/g, (ch) => `%${ch.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`);
const unesc = (s: string) => s.replace(/%(25|3A|3B|2C)/gi, (_, h: string) => String.fromCharCode(parseInt(h, 16)));

export function encodeFilters(filters: readonly ListFilter[]): string {
  return filters
    .map((f) => {
      const v = f.v === undefined ? "" : Array.isArray(f.v) ? f.v.map(esc).join(",") : typeof f.v === "boolean" ? String(f.v) : esc(f.v);
      return v === "" ? `${esc(f.c)}:${f.o}` : `${esc(f.c)}:${f.o}:${v}`;
    })
    .join(";");
}

export function decodeFilters(columns: readonly ListColumnSpec[], raw: unknown): ListFilter[] {
  if (typeof raw !== "string" || !raw || raw.length > 4000) return [];
  const out: ListFilter[] = [];
  for (const part of raw.split(";")) {
    const [c, o, ...rest] = part.split(":");
    if (!c || !o) continue;
    const col = columns.find((x) => x.key === unesc(c));
    if (!col) continue;
    const value = rest.join(":");
    let v: FilterValue | undefined;
    if (o === "empty" || o === "notempty") v = undefined;
    else if (col.type === "enum" || o === "between") v = value.split(",").map(unesc);
    else if (col.type === "bool") v = value === "true" ? true : value === "false" ? false : "";
    else v = unesc(value);
    const f: ListFilter = v === undefined ? { c: col.key, o: o as FilterOp } : { c: col.key, o: o as FilterOp, v };
    out.push(f);
  }
  return cleanFilters(columns, out);
}

export function encodeSort(sort: ListSort | null): string {
  return sort ? `${sort.dir === "desc" ? "-" : ""}${sort.col}` : "";
}

export function decodeSort(columns: readonly ListColumnSpec[], raw: unknown): ListSort | null {
  if (typeof raw !== "string" || !raw) return null;
  const desc = raw.startsWith("-");
  return validSort(columns, { col: desc ? raw.slice(1) : raw, dir: desc ? "desc" : "asc" });
}

/** The RPC's p_sort ("-date"), or null for the list's default order. */
export function sortParam(sort: ListSort | null): string | null {
  return sort ? encodeSort(sort) : null;
}
